import { flow } from "fp-ts/lib/function";
import { type IntlShape } from "react-intl";

import {
    DestinationType,
    type DigitalGood,
    type Distribution,
    type LensDestination,
    type MyLensesLensStatus,
} from "../../../gql-generated/types";
import { makeDisplayStatus } from "../../state/makeDisplayStatus";
import { makeLensActionsConfigBasedOnStatus } from "../../state/makeLensActionsConfigBasedOnStatus";
import { type LensProps } from "../LensProps";

import { messages } from "./messages.generated";

export interface DeleteLensPermission {
    isPermitted: boolean;
    reason?: string;
}

const {
    lensFolderLensCreatorRoleLensDeletionError,
    lensWithDigitalGoodsDeletionError,
    lensAdAccountDistributionLensDeletionError,
    lensWithStatusDeletionError,
} = messages;

const setDeleteLensPermissionBasedOnDigitalGoods =
    (digitalGoods: readonly DigitalGood[], intl: IntlShape) =>
    (deleteLensPermission: DeleteLensPermission): DeleteLensPermission => {
        if (!deleteLensPermission.isPermitted) {
            return deleteLensPermission;
        }

        if (digitalGoods.some((dg) => !!dg.sku)) {
            return {
                isPermitted: false,
                reason: intl.formatMessage(lensWithDigitalGoodsDeletionError),
            };
        }

        return deleteLensPermission;
    };

const setDeleteLensPermissionBasedOnRoles =
    (isPermittedToDeleteLensFolderLensBasedOnRole: boolean, intl: IntlShape) =>
    (deleteLensPermission: DeleteLensPermission): DeleteLensPermission => {
        if (!deleteLensPermission.isPermitted) {
            return deleteLensPermission;
        }

        if (!isPermittedToDeleteLensFolderLensBasedOnRole) {
            return {
                isPermitted: false,
                reason: intl.formatMessage(lensFolderLensCreatorRoleLensDeletionError),
            };
        }

        return deleteLensPermission;
    };

const setDeleteLensPermissionBasedOnAdAccountDistribution =
    (distributionsList: readonly Distribution[], intl: IntlShape) =>
    (deleteLensPermission: DeleteLensPermission): DeleteLensPermission => {
        if (!deleteLensPermission.isPermitted) {
            return deleteLensPermission;
        }
        const isLensDistributedToAdAccount = distributionsList.some(
            (d) => d.destinationType === DestinationType.DestinationTypeAdAccount,
        );
        if (isLensDistributedToAdAccount) {
            return {
                isPermitted: false,
                reason: intl.formatMessage(lensAdAccountDistributionLensDeletionError),
            };
        }

        return deleteLensPermission;
    };

const setDeleteLensPermissionBasedOnStatus =
    (status: MyLensesLensStatus, intl: IntlShape, destinations: readonly LensDestination[]) =>
    (deleteLensPermission: DeleteLensPermission): DeleteLensPermission => {
        if (!deleteLensPermission.isPermitted) {
            return deleteLensPermission;
        }

        const baseConfig = makeLensActionsConfigBasedOnStatus(status);

        if (!baseConfig.deleteLens) {
            const { name } = makeDisplayStatus(status, destinations);
            return {
                isPermitted: false,
                reason: intl.formatMessage(lensWithStatusDeletionError, { status: name }),
            };
        }

        return deleteLensPermission;
    };

export const makeDeleteLensPermissions = (
    { digitalGoodsList, distributionsList, status }: LensProps,
    intl: IntlShape,
    isPermittedToDeleteLensFolderLensBasedOnRole: boolean,
    destinations: readonly LensDestination[] = [],
): DeleteLensPermission => {
    // order of priority - higher order in the pipeline takes priority over the lower
    // only one reason message will be shown
    return flow(
        setDeleteLensPermissionBasedOnDigitalGoods(digitalGoodsList, intl),
        setDeleteLensPermissionBasedOnRoles(isPermittedToDeleteLensFolderLensBasedOnRole, intl),
        setDeleteLensPermissionBasedOnAdAccountDistribution(distributionsList, intl),
        setDeleteLensPermissionBasedOnStatus(status, intl, destinations),
    )({ isPermitted: true });
};
